/** Utilities for filtering and sorting tasks based on the active view (e.g., plan, organize). */

/**
 * Filters a list of tasks for the 'plan' and 'horizon' views by removing tasks with status 'Backlog'.
 *
 * @param {Array<Object>} tasks - The list of tasks to filter.
 * @returns {Array<Object>} The filtered list of tasks.
 */
export const filterViewTasks = (tasks) => {
  if (!tasks) return [];
  return tasks.filter((task) => task.status !== 'Backlog');
};

/**
 * Sorts a list of tasks for the 'organize' view, moving 'Backlog' items to the end.
 *
 * @param {Array<Object>} tasks - The list of tasks to sort.
 * @returns {Array<Object>} A new array of tasks, sorted as required.
 */
export const sortOrganizeViewTasks = (tasks) => {
  if (!tasks) return [];
  return [...tasks].sort((a, b) => {
    const aIsBacklog = a.status === 'Backlog';
    const bIsBacklog = b.status === 'Backlog';

    if (aIsBacklog === bIsBacklog) {
      return 0; // Keep original order if both are/aren't Backlog
    }
    return aIsBacklog ? 1 : -1; // Move a to the end if it's Backlog
  });
};

/**
 * Centrally manages the filtering and sorting of tasks based on the view string.
 *
 * @param {Array<Object>} allTasks - The unfiltered list of all tasks.
 * @param {string} view - The name of the view (e.g., 'plan', 'horizon', 'organize').
 * @returns {Array<Object>} The processed list of tasks.
 */
export const getProcessedTasksForView = (allTasks, view) => {
  if (!allTasks) return [];

  switch (view) {
    case 'plan':
    case 'horizon': {
      const filtered = filterViewTasks(allTasks);
      return filtered;
    }

    case 'organize':
      return sortOrganizeViewTasks(allTasks);

    default:
      // For any other view, return the tasks as they are.
      return allTasks;
  }
};
